import { isEmpty } from "lodash";
import { SORT_DIRECTION } from "../library/antdHelpers";
import {
  ALL_DEPARTMENTS_VALUE,
  isTemplatesV2,
  LEGACY_SCOPE_TEMPLATE_TYPES,
  TEMPLATE_DEFAULT_CHANNELS,
  TEMPLATE_LEVELS,
  TEMPLATE_LEVEL_LABELS,
  TEMPLATE_SCOPES,
  TEMPLATE_SCOPE_COLUMN_LABELS,
  TEMPLATE_TYPES,
  TEMPLATE_TYPE_LABELS,
} from "../constant/TemplateConstant";

/** Coerce a form/session value to a positive Int, or null. GraphQL Int args
 * reject the strings these values arrive as. */
export const toTemplateId = value => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

export const toTemplateIdList = values =>
  (Array.isArray(values) ? values : [])
    .map(toTemplateId)
    .filter(Boolean)
    .filter((id, index, list) => list.indexOf(id) === index);

/**
 * The active location id, or null when it cannot be resolved to a single one.
 * `currentLocationId` holds a comma-joined list while several locations are
 * selected, and a template can only be owned by one location.
 */
export const getActiveLocationId = () => {
  if (typeof sessionStorage === "undefined") return null;
  const ids = toTemplateIdList((sessionStorage.getItem("currentLocationId") || "").split(","));
  return ids.length === 1 ? ids[0] : null;
};

/**
 * The scope fields that have no column of their own.
 *
 * `level`, the per-location department `scopes` and `location_ids` are stored
 * together in the template's `extraData` JSONB, and `scopes` in particular is
 * a mutation *input* type — the query cannot select it, so `extraData` is the
 * only way a listed row carries its department scopes back to the client.
 *
 * Comes back as an object on the GraphQL side and as a string wherever the
 * column is serialised verbatim, with the API's snake_case keys — so both
 * shapes are accepted here rather than at every call site.
 */
export const getTemplateExtraData = template => {
  const raw = template?.extraData;
  if (!raw) return {};
  if (typeof raw === "string") {
    try {
      return JSON.parse(raw) || {};
    } catch {
      return {};
    }
  }
  return typeof raw === "object" ? raw : {};
};

/** Normalise a list of `{ locationId, departmentIds, allDepartments }` rows in either casing. */
const readScopeRows = rows =>
  (Array.isArray(rows) ? rows : [])
    .map(scope => ({
      locationId: toTemplateId(scope?.locationId ?? scope?.location_id),
      departmentIds: toTemplateIdList(scope?.departmentIds ?? scope?.department_ids),
      allDepartments: !!(scope?.allDepartments ?? scope?.all_departments),
    }))
    .filter(scope => scope.locationId);

/**
 * Level of an existing template. Rows created before `level` existed carry no
 * value, so fall back to inferring it from the legacy scope columns.
 */
export const deriveTemplateLevel = template => {
  if (template?.level) return template.level;
  const extraLevel = getTemplateExtraData(template)?.level;
  if (extraLevel) return extraLevel;
  if (template?.scopes?.length) return TEMPLATE_LEVELS.DEPARTMENT;
  if (template?.isCompanyShared) return TEMPLATE_LEVELS.COMPANY;
  if (toTemplateId(template?.departmentId)) return TEMPLATE_LEVELS.DEPARTMENT;
  if (toTemplateId(template?.opportunityTypeId)) return TEMPLATE_LEVELS.OPPORTUNITY_TYPE;
  if (toTemplateId(template?.locationId) || toTemplateId(template?.location?.id)) {
    return TEMPLATE_LEVELS.LOCATION;
  }
  return TEMPLATE_LEVELS.USER;
};

export const getTemplateLocationId = template =>
  toTemplateId(template?.locationId) ?? toTemplateId(template?.location?.id);

/**
 * Locations a LOCATION-level template is shared with. `locationIds` is the
 * column; older rows — and any environment where it reads back empty — keep
 * the same list in `extraData.location_ids`, and a row written before the list
 * existed has only the single `locationId`.
 *
 * The single-column fallback is deliberately gated on the level: a private
 * template can carry a stale `locationId`, and treating that as a share would
 * put the author's own template in front of everyone at that location.
 */
export const getTemplateLocationIds = template => {
  const fromColumn = toTemplateIdList(template?.locationIds);
  if (fromColumn.length) return fromColumn;
  const extra = getTemplateExtraData(template);
  const fromExtraData = toTemplateIdList(extra?.locationIds ?? extra?.location_ids);
  if (fromExtraData.length) return fromExtraData;
  if (deriveTemplateLevel(template) !== TEMPLATE_LEVELS.LOCATION) return [];
  const single = getTemplateLocationId(template);
  return single ? [single] : [];
};

/**
 * Per-location department scopes.
 *
 * `scopes` is a mutation input, so the query cannot select it — a listed row
 * carries its scopes in `extraData` instead. Reading that first is what keeps
 * a DEPARTMENT-level template editable: without it the form comes up with an
 * empty location picker, and re-saving the row (which is all the active toggle
 * does) sends `level: DEPARTMENT` with no scopes, which the server rejects.
 * The flattened locationId/departmentId columns are the last resort, for rows
 * written before either existed.
 */
export function getTemplateScopes(template) {
  const fromField = readScopeRows(template?.scopes);
  if (fromField.length) return fromField;

  const fromExtraData = readScopeRows(getTemplateExtraData(template)?.scopes);
  if (fromExtraData.length) return fromExtraData;

  const legacyLocationId = getTemplateLocationId(template);
  const legacyDepartments = toTemplateIdList(
    template?.departmentIds?.length ? template.departmentIds : [template?.departmentId]
  );
  if (legacyLocationId && legacyDepartments.length) {
    return [
      { locationId: legacyLocationId, departmentIds: legacyDepartments, allDepartments: false },
    ];
  }
  return [];
}

/**
 * Departments a template is scoped to, flattened across its per-location
 * scopes. Rows written before multi-select carry only `departmentId`.
 */
export const getTemplateDepartmentIds = template => {
  const fromScopes = getTemplateScopes(template).flatMap(scope =>
    toTemplateIdList(scope?.departmentIds)
  );
  if (fromScopes.length) return fromScopes;
  const ids = toTemplateIdList(template?.departmentIds);
  if (ids.length) return ids;
  const single = toTemplateId(template?.departmentId);
  return single ? [single] : [];
};

/** Drop rows with no location, and empty department picks that aren't "all". */
export const normaliseDepartmentScopes = scopes =>
  (Array.isArray(scopes) ? scopes : [])
    .map(scope => {
      const picked = Array.isArray(scope?.departmentIds) ? scope.departmentIds : [];
      // The picker expresses "all" as a sentinel option rather than a separate
      // flag, so it arrives here inside departmentIds.
      const allDepartments = !!scope?.allDepartments || picked.includes(ALL_DEPARTMENTS_VALUE);
      return {
        locationId: toTemplateId(scope?.locationId),
        // "all departments" is the flag, not an enumerated list — sending both
        // would let the two disagree.
        departmentIds: allDepartments ? [] : toTemplateIdList(scope?.departmentIds),
        allDepartments,
      };
    })
    .filter(scope => scope.locationId && (scope.allDepartments || scope.departmentIds.length));

/**
 * Build the scope half of the save payload from the selected level.
 *
 * Each level owns a different set of arguments, so every one of them is
 * emitted explicitly — empty rather than omitted — and changing a template's
 * level on edit clears what the previous level owned instead of leaving it
 * reachable through two scopes at once.
 *
 * - USER      → locationIds; empty means every location the user can reach.
 *               `isCompanyShared` is the rep's opt-in to publish it company-wide.
 * - LOCATION  → locationIds; the locations that own the template.
 * - DEPARTMENT→ scopes; one entry per location, each either an explicit
 *               department list or allDepartments.
 * - COMPANY   → nothing to narrow.
 */
export const buildTemplateScopePayload = ({
  level,
  companyId,
  locationIds,
  scopes,
  opportunityTypeId,
  isCompanyShared = false,
} = {}) => {
  const base = {
    level,
    companyId: toTemplateId(companyId),
    locationIds: [],
    scopes: [],
    opportunityTypeId: null,
    isCompanyShared: false,
  };

  switch (level) {
    case TEMPLATE_LEVELS.COMPANY:
      // Level already says company-wide; the legacy flag is kept in step for
      // anything still reading it (older rows, the scope description).
      return { ...base, isCompanyShared: true };
    case TEMPLATE_LEVELS.LOCATION:
      return { ...base, locationIds: toTemplateIdList(locationIds) };
    case TEMPLATE_LEVELS.DEPARTMENT:
      return { ...base, scopes: normaliseDepartmentScopes(scopes) };
    case TEMPLATE_LEVELS.OPPORTUNITY_TYPE:
      return {
        ...base,
        locationIds: toTemplateIdList(locationIds),
        opportunityTypeId: toTemplateId(opportunityTypeId),
      };
    default:
      return {
        ...base,
        level: TEMPLATE_LEVELS.USER,
        locationIds: toTemplateIdList(locationIds),
        isCompanyShared: !!isCompanyShared,
      };
  }
};

/** A template needs a subject line only when it is sent as an email. */
export const templateNeedsSubject = templateType => templateType === TEMPLATE_TYPES.EMAIL;

/**
 * The complete variables object for saveEngagementMessageTemplate. Kept here
 * rather than in the form so the payload each level produces is testable
 * without rendering anything.
 */
export const buildTemplateSaveVariables = ({
  id,
  userId,
  companyId,
  title,
  message,
  subject,
  templateType,
  isActive = true,
  level,
  locationIds,
  scopes,
  opportunityTypeId,
  isCompanyShared,
} = {}) => {
  const common = {
    id: toTemplateId(id),
    userId: toTemplateId(userId),
    title,
    message,
    // Only an email template carries a subject; anything else would persist a
    // stale one after a type change.
    subject: templateNeedsSubject(templateType) ? subject || null : null,
    templateType: templateType || null,
    isActive,
  };

  if (isTemplatesV2()) {
    return {
      ...common,
      ...buildTemplateScopePayload({
        level,
        companyId,
        locationIds,
        scopes,
        opportunityTypeId,
        isCompanyShared,
      }),
    };
  }

  // Pre-deploy schema: one locationId and the isCompanyShared flag are the only
  // scope it has. A multi-location or multi-department pick collapses to its
  // first location, which is the closest the old columns can express.
  const scope = buildTemplateScopePayload({
    level,
    companyId,
    locationIds,
    scopes,
    opportunityTypeId,
    isCompanyShared,
  });
  const firstScopeLocation = scope.scopes?.[0]?.locationId ?? null;
  return {
    ...common,
    companyId: scope.companyId,
    isCompanyShared: scope.isCompanyShared,
    locationId:
      scope.level === TEMPLATE_LEVELS.COMPANY ? null : scope.locationIds?.[0] ?? firstScopeLocation,
  };
};

/** Human-readable scope for the templates table. */
export const describeTemplateScope = (template, { locationLabel } = {}) => {
  const level = deriveTemplateLevel(template);
  const label = TEMPLATE_LEVEL_LABELS[level] || level;
  const sharedLocations = toTemplateIdList(template?.locationIds).length;

  if (level === TEMPLATE_LEVELS.LOCATION) {
    const name = locationLabel || template?.location?.title;
    if (name) {
      const extra = Math.max(sharedLocations - 1, 0);
      return extra ? `${label}: ${name} +${extra}` : `${label}: ${name}`;
    }
    return sharedLocations ? `${label} (${sharedLocations})` : label;
  }
  if (level === TEMPLATE_LEVELS.DEPARTMENT) {
    const scopes = getTemplateScopes(template);
    const departments = getTemplateDepartmentIds(template).length;
    const anyAll = scopes.some(scope => scope.allDepartments);
    if (!scopes.length) return label;
    const detail = anyAll && !departments ? "All" : departments;
    return `${label}: ${detail} · ${scopes.length} ${
      scopes.length === 1 ? "location" : "locations"
    }`;
  }
  if (level === TEMPLATE_LEVELS.USER) {
    if (template?.isCompanyShared) return `${label} · Shared`;
    // No locations means every location the user can reach.
    return sharedLocations ? `${label} (${sharedLocations})` : `${label} · All`;
  }
  return label;
};

export const isTemplateMessageEmpty = value =>
  isEmpty(
    String(value ?? "")
      .replace(/<[^>]*>/g, "")
      .replace(/&nbsp;/g, " ")
      .trim()
  );

/**
 * `engagementMessageTemplates` returns a `{ count, data }` envelope on the new
 * contract and a bare list on the pre-deploy one. Everything reading it goes
 * through these, so the app works either way.
 */
export const getTemplateList = data => {
  const result = data?.engagementMessageTemplates;
  if (Array.isArray(result)) return result;
  return result?.data || [];
};

export const getTemplateCount = data => {
  const result = data?.engagementMessageTemplates;
  if (Array.isArray(result)) return result.length;
  return result?.count || 0;
};

/**
 * Turn a UI scope choice into `engagementMessageTemplates` arguments.
 *
 * Scope used to ride on the `templateType` argument as a pseudo-type
 * (ALL/OWN/COMPANY/…); it is now `level` plus `isGlobal`, leaving
 * `templateType` free to mean the actual type. Narrowing within a level is
 * optional — locationId, departmentId and opportunityTypeId are only sent when
 * the caller supplies them, so a level on its own returns everything at that
 * level.
 *
 * Every key is present in the result, `undefined` where unused, so callers can
 * spread it over a base variables object without leaving a stale filter behind.
 */
export const buildTemplateScopeVariables = (
  scope,
  { companyId, locationId, departmentId, opportunityTypeId } = {}
) => {
  const narrowedLocationId = toTemplateId(locationId);

  const base = {
    level: undefined,
    isGlobal: undefined,
    companyId: toTemplateId(companyId),
    locationId: narrowedLocationId ?? undefined,
    departmentId: undefined,
    opportunityTypeId: undefined,
  };

  switch (scope) {
    case TEMPLATE_SCOPES.MINE:
      return { ...base, level: TEMPLATE_LEVELS.USER };
    case TEMPLATE_SCOPES.COMPANY:
      return { ...base, level: TEMPLATE_LEVELS.COMPANY };
    case TEMPLATE_SCOPES.LOCATION:
      return {
        ...base,
        level: TEMPLATE_LEVELS.LOCATION,
        locationId: narrowedLocationId ?? getActiveLocationId() ?? undefined,
      };
    case TEMPLATE_SCOPES.DEPARTMENT:
      return {
        ...base,
        level: TEMPLATE_LEVELS.DEPARTMENT,
        departmentId: toTemplateId(departmentId) ?? undefined,
      };
    case TEMPLATE_SCOPES.OPPORTUNITY_TYPE:
      return {
        ...base,
        level: TEMPLATE_LEVELS.OPPORTUNITY_TYPE,
        opportunityTypeId: toTemplateId(opportunityTypeId) ?? undefined,
      };
    case TEMPLATE_SCOPES.GLOBAL:
      // Global templates belong to no company and no location, so nothing may
      // narrow them.
      return { ...base, companyId: undefined, locationId: undefined, isGlobal: true };
    default:
      // ALL — every level within the company.
      return base;
  }
};

/**
 * Variables for the templates query, in whichever contract the API speaks.
 *
 * New contract: `level` / `isGlobal` select the tier and `templateType` filters
 * by the template's actual type, with paging server-side.
 * Pre-deploy contract: `templateType` carries the scope instead, so it cannot
 * also filter by type — that falls to `selectTemplatePage`, and there is no
 * paging at all.
 *
 * Pass `scope: null` when the caller wants a plain type filter (the email
 * pickers), which both contracts express the same way.
 */
export const buildTemplateQueryVariables = ({
  scope = TEMPLATE_SCOPES.ALL,
  companyId,
  locationId,
  userId,
  templateType,
  search,
  isActive,
  page,
  pageSize,
  orderBy,
  orderDirection,
} = {}) => {
  if (isTemplatesV2()) {
    return {
      ...buildTemplateScopeVariables(scope || TEMPLATE_SCOPES.ALL, { companyId, locationId }),
      templateType: templateType || undefined,
      search: search || undefined,
      isActive,
      page,
      pageSize,
      // Only send a sort when one is active; the server orders by id otherwise.
      orderBy: orderBy || undefined,
      orderDirection: orderBy ? orderDirection || SORT_DIRECTION.ASC : undefined,
    };
  }

  const isGlobalScope = scope === TEMPLATE_SCOPES.GLOBAL;
  return {
    // Required on the old schema, and the scope lives here. With no scope the
    // caller's own type is passed through, which is how the email pickers have
    // always queried.
    templateType: scope ? LEGACY_SCOPE_TEMPLATE_TYPES[scope] || "ALL" : templateType || "ALL",
    companyId: isGlobalScope ? undefined : toTemplateId(companyId),
    userId: !scope || scope === TEMPLATE_SCOPES.MINE ? toTemplateId(userId) : undefined,
    locationId: isGlobalScope ? undefined : toTemplateId(locationId) ?? undefined,
    search: search || undefined,
    isActive,
  };
};

/**
 * The rows and total to render. On the new contract the server has already
 * filtered and paged; on the old one both happen here, because its
 * `templateType` argument was spent on the scope.
 */
export const selectTemplatePage = (data, { page, pageSize, templateType } = {}) => {
  const rows = getTemplateList(data);
  if (isTemplatesV2()) return { rows, total: getTemplateCount(data) };

  const filtered = templateType ? rows.filter(row => row.templateType === templateType) : rows;
  if (!page || !pageSize) return { rows: filtered, total: filtered.length };
  const start = (page - 1) * pageSize;
  return { rows: filtered.slice(start, start + pageSize), total: filtered.length };
};

/**
 * The short scope wording the templates table shows. `describeTemplateScope`
 * spells out the locations and departments behind it, which the table hands to
 * a tooltip rather than putting in the cell.
 */
export const getTemplateScopeLabel = (template, { currentUserId } = {}) => {
  // Rows written before `level` existed can carry a stale level alongside
  // `isCompanyShared: true`. The server's COMPANY scope filter matches on the
  // flag, so a row it returns as company-wide has to be labelled that way —
  // otherwise filtering by Company lists rows the column calls something else.
  // Only the label is decided here; `deriveTemplateLevel` still owns what a
  // save writes back.
  if (template?.isCompanyShared) return TEMPLATE_SCOPE_COLUMN_LABELS[TEMPLATE_LEVELS.COMPANY];
  const level = deriveTemplateLevel(template);
  // "My template" is only true from the owner's seat. A company admin looking
  // at someone else's personal template needs it to read as personal, not as
  // theirs.
  if (level === TEMPLATE_LEVELS.USER && currentUserId != null) {
    return +template?.userId === +currentUserId ? "My template" : "Personal";
  }
  return TEMPLATE_SCOPE_COLUMN_LABELS[level] || "";
};

/**
 * When a template was last touched.
 *
 * `updatedOn` is only written on edit, so a template that has never been
 * changed since it was created has none — falling back to `createdOn` keeps
 * the column showing when the template last meaningfully changed rather than a
 * placeholder.
 */
export const getTemplateUpdatedOn = template => template?.updatedOn || template?.createdOn || null;

/**
 * A template's type, defaulting to Engagement.
 *
 * `templateType` is nullable, and rows written before it existed carry no
 * value. Engagement is what an untyped template is in practice — it is the
 * type the form creates by default — so the whole module reads it that way
 * rather than showing a blank cell and dropping the row out of its category.
 */
export const getTemplateType = template => template?.templateType || TEMPLATE_TYPES.ENGAGEMENT;

/**
 * Category chips for the picker: every type present in the list, in the order
 * the type enum declares, each with how many templates carry it.
 *
 * Counts come from the rows in hand rather than the server — the picker fetches
 * one large page and filters it client-side, so the two agree. A type the
 * build does not know (a user-created one) still gets a chip, keyed by its raw
 * value, so it cannot become unreachable.
 */
export const getTemplateTypeCounts = templates => {
  const rows = Array.isArray(templates) ? templates : [];
  const counts = new Map();
  rows.forEach(template => {
    const type = getTemplateType(template);
    counts.set(type, (counts.get(type) || 0) + 1);
  });

  const known = Object.values(TEMPLATE_TYPES).filter(type => counts.has(type));
  const custom = [...counts.keys()].filter(type => !known.includes(type)).sort();

  return [...known, ...custom].map(value => ({
    value,
    label: TEMPLATE_TYPE_LABELS[value] || value,
    count: counts.get(value),
  }));
};

/** Channels a template is sent on, defaulted for rows written before channels existed. */
export const getTemplateChannels = template => {
  const channels = Array.isArray(template?.channels) ? template.channels.filter(Boolean) : [];
  return channels.length ? channels : TEMPLATE_DEFAULT_CHANNELS;
};

/** Case-insensitive duplicate check for a user-created type name. */
export const findTemplateTypeByName = (options, name) => {
  const needle = String(name ?? "")
    .trim()
    .toLowerCase();
  if (!needle) return null;
  return (
    (options || []).find(option => String(option?.label ?? "").toLowerCase() === needle) || null
  );
};

/**
 * A template body as readable plain text.
 *
 * Bodies are authored in the rich-text editor and come back with markup in
 * them — `<br/>` for the author's line breaks, and HTML entities such as
 * `&nbsp;`. The picker and the table render the preview as text rather than
 * markup, so those have to be resolved here or they show through raw.
 *
 * Deliberately regex-based rather than DOM-based: this runs during Gatsby's
 * SSR build too, where there is no `document` to parse with.
 */
const HTML_ENTITIES = {
  "&nbsp;": " ",
  "&amp;": "&",
  "&lt;": "<",
  "&gt;": ">",
  "&quot;": '"',
  "&#39;": "'",
  "&apos;": "'",
};

export const toPlainTemplateText = message =>
  String(message ?? "")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>\s*<p[^>]*>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&#(\d+);/g, (_, code) => String.fromCharCode(Number(code)))
    .replace(/&[a-z]+;|&#39;/gi, entity => HTML_ENTITIES[entity.toLowerCase()] ?? entity);
